import React, { useEffect, useState } from 'react';
import { X } from 'lucide-react';
type LegalContentType = 'privacy' | 'terms' | null;
export function Footer() {
  const [modalContent, setModalContent] = useState<LegalContentType>(null);
  const [isAnimating, setIsAnimating] = useState(false);
  // Handle body scroll lock when modal is open
  useEffect(() => {
    if (modalContent) {
      document.body.style.overflow = 'hidden';
      setIsAnimating(true);
    } else {
      document.body.style.overflow = 'unset';
      setIsAnimating(false);
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [modalContent]);
  const closeModal = () => {
    setIsAnimating(false);
    setTimeout(() => setModalContent(null), 300);
  };
  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      closeModal();
    }
  };
  return <>
      <footer className="bg-white py-12 border-t border-cream-dark/10" data-id="element-36">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col items-center justify-center space-y-6" data-id="element-37">
          {/* Copyright */}
          <p className="text-warm-gray text-sm font-sans" data-id="element-38">
            © 2026 Menopausia Sin Castigo. Todos los derechos reservados.
          </p>

          {/* Legal Links */}
          <div className="flex flex-wrap justify-center items-center gap-x-6 gap-y-2 text-sm font-sans" data-id="element-39">
            <button onClick={() => setModalContent('privacy')} className="text-warm-gray hover:text-teal transition-colors duration-200 border-b border-transparent hover:border-teal" data-id="element-40">
              Política de Privacidad
            </button>
            <span className="text-warm-gray/30 hidden sm:inline" data-id="element-41">•</span>
            <button onClick={() => setModalContent('terms')} className="text-warm-gray hover:text-teal transition-colors duration-200 border-b border-transparent hover:border-teal" data-id="element-42">
              Términos y Condiciones
            </button>
          </div>
        </div>
      </footer>

      {/* Modal Overlay */}
      {modalContent && <div className={`fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6 transition-opacity duration-300 ${isAnimating ? 'opacity-100' : 'opacity-0'}`} role="dialog" aria-modal="true" data-id="element-43">
          {/* Backdrop */}
          <div className="absolute inset-0 bg-warm-dark/60 backdrop-blur-sm" onClick={handleBackdropClick} data-id="element-44" />

          {/* Modal Card */}
          <div className={`relative w-full max-w-2xl bg-cream-light rounded-xl shadow-2xl flex flex-col max-h-[85vh] transform transition-all duration-300 ${isAnimating ? 'scale-100 translate-y-0' : 'scale-95 translate-y-4'}`} data-id="element-45">
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-warm-gray/10 bg-white/50 rounded-t-xl" data-id="element-46">
              <h2 className="text-2xl font-serif text-teal-dark font-bold" data-id="element-47">
                {modalContent === 'privacy' ? 'Política de Privacidad' : 'Términos y Condiciones'}
              </h2>
              <button onClick={closeModal} className="p-2 text-warm-gray hover:text-teal hover:bg-teal-light/50 rounded-full transition-colors" aria-label="Cerrar" data-id="element-48">
                <X className="w-6 h-6" data-id="element-49" />
              </button>
            </div>

            {/* Scrollable Content */}
            <div className="overflow-y-auto p-6 sm:p-8 space-y-6 text-warm-dark font-sans leading-relaxed" data-id="element-50">
              {modalContent === 'privacy' ? <PrivacyContent data-id="element-51" /> : <TermsContent data-id="element-52" />}
            </div>

            {/* Footer gradient for scroll hint */}
            <div className="absolute bottom-0 left-0 right-0 h-12 bg-gradient-to-t from-cream-light to-transparent pointer-events-none rounded-b-xl" data-id="element-53" />
          </div>
        </div>}
    </>;
}
function PrivacyContent() {
  return <div className="space-y-6 text-sm sm:text-base" data-id="element-54">
      <section data-id="element-55">
        <h3 className="text-lg font-serif font-bold text-teal mb-2" data-id="element-56">
          1. Introducción
        </h3>
        <p data-id="element-57">
          En Menopausia Sin Castigo, valoramos y respetamos su privacidad. Esta
          Política de Privacidad describe cómo recopilamos, utilizamos y
          protegemos su información personal cuando utiliza nuestros servicios
          digitales y sitio web.
        </p>
      </section>

      <section data-id="element-58">
        <h3 className="text-lg font-serif font-bold text-teal mb-2" data-id="element-59">
          2. Recopilación de Información
        </h3>
        <p data-id="element-60">
          Recopilamos información que usted nos proporciona voluntariamente,
          como su nombre y dirección de correo electrónico al suscribirse a
          nuestro boletín o adquirir nuestros productos. También podemos
          recopilar datos técnicos de navegación de forma anónima para mejorar
          la experiencia del usuario.
        </p>
      </section>

      <section data-id="element-61">
        <h3 className="text-lg font-serif font-bold text-teal mb-2" data-id="element-62">
          3. Uso de la Información
        </h3>
        <p data-id="element-63">Utilizamos su información para:</p>
        <ul className="list-disc pl-5 mt-2 space-y-1 text-warm-gray" data-id="element-64">
          <li data-id="element-65">Proporcionar y mantener nuestros servicios.</li>
          <li data-id="element-66">
            Enviarle actualizaciones, boletines y material educativo relevante.
          </li>
          <li data-id="element-67">Mejorar nuestro sitio web y contenido.</li>
          <li data-id="element-68">Cumplir con obligaciones legales.</li>
        </ul>
      </section>

      <section data-id="element-69">
        <h3 className="text-lg font-serif font-bold text-teal mb-2" data-id="element-70">
          4. Protección de Datos
        </h3>
        <p data-id="element-71">
          Implementamos medidas de seguridad técnicas y organizativas apropiadas
          para proteger sus datos personales contra el acceso no autorizado, la
          alteración, la divulgación o la destrucción.
        </p>
      </section>

      <section data-id="element-72">
        <h3 className="text-lg font-serif font-bold text-teal mb-2" data-id="element-73">
          5. Cookies
        </h3>
        <p data-id="element-74">
          Utilizamos cookies esenciales para el funcionamiento del sitio y
          cookies analíticas para entender cómo los usuarios interactúan con
          nuestro contenido. Usted puede configurar su navegador para rechazar
          todas las cookies.
        </p>
      </section>

      <section data-id="element-75">
        <h3 className="text-lg font-serif font-bold text-teal mb-2" data-id="element-76">
          6. Derechos del Usuario
        </h3>
        <p data-id="element-77">
          Usted tiene derecho a acceder, rectificar o eliminar su información
          personal en cualquier momento. También puede optar por dejar de
          recibir nuestras comunicaciones siguiendo el enlace de "darse de baja"
          en nuestros correos electrónicos.
        </p>
      </section>
    </div>;
}
function TermsContent() {
  return <div className="space-y-6 text-sm sm:text-base" data-id="element-78">
      <section data-id="element-79">
        <h3 className="text-lg font-serif font-bold text-teal mb-2" data-id="element-80">
          1. Aceptación de los Términos
        </h3>
        <p data-id="element-81">
          Al acceder y utilizar el sitio web de Menopausia Sin Castigo, usted
          acepta estar legalmente vinculado por estos Términos y Condiciones. Si
          no está de acuerdo con alguno de estos términos, le rogamos que no
          utilice nuestro sitio.
        </p>
      </section>

      <section data-id="element-82">
        <h3 className="text-lg font-serif font-bold text-teal mb-2" data-id="element-83">
          2. Descargo de Responsabilidad Médica
        </h3>
        <div className="bg-gold/10 p-4 rounded-lg border border-gold/20" data-id="element-84">
          <p className="font-medium text-warm-dark" data-id="element-85">
            IMPORTANTE: El contenido de este sitio web es únicamente con fines
            informativos y educativos. No sustituye el consejo, diagnóstico o
            tratamiento médico profesional.
          </p>
          <p className="mt-2" data-id="element-86">
            Siempre busque el consejo de su médico u otro proveedor de salud
            calificado con cualquier pregunta que pueda tener sobre una
            condición médica. Nunca ignore el consejo médico profesional ni
            demore en buscarlo debido a algo que haya leído en este sitio web.
          </p>
        </div>
      </section>

      <section data-id="element-87">
        <h3 className="text-lg font-serif font-bold text-teal mb-2" data-id="element-88">
          3. Propiedad Intelectual
        </h3>
        <p data-id="element-89">
          Todo el contenido, incluyendo textos, gráficos, logotipos, imágenes y
          software, es propiedad de Menopausia Sin Castigo o de sus proveedores
          de contenido y está protegido por las leyes de propiedad intelectual
          internacionales.
        </p>
      </section>

      <section data-id="element-90">
        <h3 className="text-lg font-serif font-bold text-teal mb-2" data-id="element-91">
          4. Uso Permitido
        </h3>
        <p data-id="element-92">
          Se le concede una licencia limitada para acceder y hacer uso personal
          de este sitio. No está permitido descargar (excepto el almacenamiento
          en caché de páginas) o modificar ninguna parte del mismo sin nuestro
          consentimiento expreso por escrito.
        </p>
      </section>

      <section data-id="element-93">
        <h3 className="text-lg font-serif font-bold text-teal mb-2" data-id="element-94">
          5. Limitación de Responsabilidad
        </h3>
        <p data-id="element-95">
          Menopausia Sin Castigo no será responsable de ningún daño directo,
          indirecto, incidental o consecuente que resulte del uso o la
          imposibilidad de uso de nuestros servicios o productos.
        </p>
      </section>

      <section data-id="element-96">
        <h3 className="text-lg font-serif font-bold text-teal mb-2" data-id="element-97">
          6. Modificaciones
        </h3>
        <p data-id="element-98">
          Nos reservamos el derecho de realizar cambios en nuestro sitio web,
          políticas y estos Términos y Condiciones en cualquier momento. Su uso
          continuado del sitio tras la publicación de cambios constituye su
          aceptación de dichos cambios.
        </p>
      </section>
    </div>;
}